import React, { useState, useRef, useEffect } from "react";
import { Button, Container, Dropdown, Card } from "react-bootstrap";
import MapList from "../assets/images/view-icon/map-list.svg";
import Map from "../assets/images/view-icon/map.svg";
import List from "../assets/images/view-icon/list.svg";
import Like from "../assets/images/like.svg";
import bed from "../assets/images/bed.svg";
import bath from "../assets/images/bath.svg";
import size from "../assets/images/scale.svg";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import Header2 from "../component/Header2";
import { BASE_URL } from "../api/constants";
import { formatRentalPeriod } from "../utils/formatRentalPeriod";
import useFavoritesList from "../hooks/useFavoritesList";
import RentHeader from "../component/RentHeader";
import FilterBar from "../component/FilterBar";
import usePropertySort from "../hooks/usePropertySort";
import { Helmet } from "react-helmet-async";

function Rent() {
  const { isFavorite, toggleFavorite } = useFavoritesList();
  const [view, setView] = useState(window.innerWidth < 768 ? "list" : "both");
  const headerRef = useRef(null);
  const filterRef = useRef(null);
  const [resultHeight, setResultHeight] = useState("60vh");
  const [sort, setSort] = useState("Sort by");

  //Listings
  const [residential_listings, setResidentialListings] = useState([]);
  const [county, setCounty] = useState("Nairobi");

  const getRentalProperties = async () => {
    try {
      // Read current URL query parameters
      const params = new URLSearchParams(window.location.search);
      const property_type = params.get("property_type") || "residential"; // default
      const listing_type = "rent"; // always rent for this page
      const category = params.get("category"); // optional: apartment, house
      const building = params.get("building"); // optional: true/false

      // Build fetch URL dynamically
      let url = `${BASE_URL}/properties?property_type=${property_type}&listing_type=${listing_type}`;
      if (category) url += `&category=${category}`;
      if (building) url += `&building=${building}`;

      const res = await fetch(url);
      const data = await res.json();
      console.log(data.data);

      if (data?.status && Array.isArray(data.data)) {
        setResidentialListings(data.data); // or setRentalListings if you have separate state
      } else {
        setResidentialListings([]); // fallback
      }
    } catch (err) {
      console.error(err);
      setResidentialListings([]); // fallback
    }
  };

  useEffect(() => {
    const updateHeight = () => {
      const headerH = headerRef.current?.offsetHeight || 0;
      const filterH = filterRef.current?.offsetHeight || 0;
      const h = Math.max(0, window.innerHeight - headerH - filterH);
      setResultHeight(`${h}px`);
    };
    updateHeight();
    getRentalProperties();

    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const sortedListings = usePropertySort(residential_listings, sort);

  const mapVisible = view === "both" || view === "map";
  const listVisible = view === "both" || view === "list";

  return (
    <>
      <Helmet>
        <title>Rent | Jambobay</title>
        <meta name="description" content="Browse rental properties in Kenya, including apartments, houses, and commercial spaces. Find your next home with Jambobay." />
        <meta name="keywords" content="Rent, Rental Properties, Apartments, Houses, Commercial Spaces, Jambobay" />
      </Helmet>
      <div className="buy-main">
        <div ref={headerRef}>
          <Header2 />
        </div>
        <div className="buy-filter" ref={filterRef}>
          <div className="view-box">
            <div className="filter-group">
              <Button
                className={`map-list d-none d-md-flex ${
                  view === "both" ? "active" : ""
                }`}
                onClick={() => setView("both")}
                aria-pressed={view === "both"}
              >
                <img src={MapList} alt="" />
              </Button>
              <Button
                className={`map ${view === "map" ? "active" : ""}`}
                onClick={() => setView("map")}
                aria-pressed={view === "map"}
              >
                <img src={Map} alt="" />
              </Button>
              <Button
                className={`list ${view === "list" ? "active" : ""}`}
                onClick={() => setView("list")}
                aria-pressed={view === "list"}
              >
                <img src={List} alt="" />
              </Button>
            </div>
          </div>
          <FilterBar onSuburbChange={(suburb) => setCounty(suburb)} />
        </div>
        <div className="result-box" style={{ height: resultHeight }}>
          <div
            className="result-map"
            style={{
              animation: mapVisible
                ? "slideInLeft 0.4s ease-in-out forwards"
                : "slideOutRight 0.4s ease-in-out forwards",
              flex: mapVisible && listVisible ? 1 : "1 1 100%",
              overflow: "hidden",
              minHeight: 0,
              display: mapVisible ? "flex" : "none",
            }}
          >
            <iframe
              title="Google Maps"
              style={{ width: "100%", height: "100%", border: 0 }}
              src={`https://maps.google.com/maps?q=${encodeURIComponent(county + ", Kenya")}&output=embed`}
              allowFullScreen
              loading="lazy"
            />
          </div>
          <div
            className={`result-list ${view === "list" ? "list-open" : ""}`}
            style={{
              animation: listVisible
                ? "slideInRight 0.4s ease-in-out forwards"
                : "slideOutLeft 0.4s ease-in-out forwards",
              flex: mapVisible && listVisible ? 1 : "1 1 100%",
              overflowY: "auto",
              minHeight: 0,
              display: listVisible ? "block" : "none",
            }}
          >
            <div className="result-list-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                <RentHeader />
              </h6>
              <span>
                {sortedListings.length} result
                {sortedListings.length === 1 ? "" : "s"}
              </span>
              <Dropdown align="end" onSelect={(key) => key && setSort(key)}>
                <Dropdown.Toggle id="sort-dropdown">
                  {sort === "Sort by" ? <>Sort by</> : <>{sort}</>}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  {[
                    "Homes for You",
                    "Price (High to Low)",
                    "Price (Low to High)",
                    "Newest",
                    "Bedrooms",
                    "Bathrooms",
                    "Square Feet",
                    "Lot Size",
                  ].map((opt) => (
                    <Dropdown.Item
                      key={opt}
                      eventKey={opt}
                      active={sort === opt}
                    >
                      {opt}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <ul>
              {residential_listings?.map((item) => (
                <li key={item.id}>
                  <Link to={`/rent-details/${item.slug}`}>
                    <div className="result-item-img position-relative">
                      <Swiper
                        modules={[Autoplay, Pagination]}
                        spaceBetween={20}
                        centeredSlides={true}
                        slidesPerGroup={1}
                        // loop={true}
                        // autoplay={{ delay: 2500, disableOnInteraction: false }}
                        pagination={{ clickable: true }}
                      >
                        {item.images.length > 0 &&
                          item.images.slice(0, 3).map((url, idx) => (
                            <SwiperSlide key={idx}>
                              <Card className="border-0">
                                <Card.Img src={url.image} />
                              </Card>
                            </SwiperSlide>
                          ))}
                      </Swiper>

                      {item.tag && (
                        <span className="recommended">{item.tag}</span>
                      )}

                      <span
                        key={item.id}
                        role="button"
                        tabIndex={0}
                        className={`wishlist ${isFavorite(item.id) ? "active" : ""}`}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleFavorite(item.id);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") toggleFavorite(item.id);
                        }}
                      >
                        <img src={Like} alt="like" />
                      </span>
                    </div>
                    <div className="result-item-info">
                      <h2>
                        KES{item.price}
                        <span> {formatRentalPeriod(item.rental_term)}</span>
                      </h2>
                      <p>{item.street_address}</p>
                      <div className="info-box">
                        <span>
                          <img src={bed} alt="" /> {item.details.bedrooms}
                        </span>
                        <span>
                          <img src={bath} alt="" /> {item.details.bathrooms}
                        </span>
                        <span>
                          <img src={size} alt="" /> {item.size_sqft}
                        </span>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <Container></Container>
      </div>
    </>
  );
}

export default Rent;
